// Vendor Imports
import _ from 'lodash';
import $ from 'jquery';

// Project Imports
import DistributionChartHelpers from './views/DistributionChartHelpers';
import SvgHistogram from './views/SvgHistogram';
import { generateSummaryTableVif } from './VisualizationCommon';
import SoqlDataProvider from './dataProviders/SoqlDataProvider';
import { getSoqlVifValidator } from './helpers/SoqlVifValidator';
import { migrateVif } from './helpers/migrateVif';
import * as ColumnFormattingHelpers from './helpers/ColumnFormattingHelpers';
import SoqlHelpers from './helpers/SoqlHelpers';
import { getDatasetUid, getDomain, getParameterOverrides } from './helpers/VifSelectors';
import { I18n } from 'common/i18n';
import formatString from 'common/js_utils/formatString';
import MetadataProvider, {
  getDisplayableColumns,
} from 'common/visualizations/dataProviders/MetadataProvider';
import { SoQLType } from 'common/types/soql';
import { type Vif, type DataRow } from './vif';
import { type BaseVisualization as BaseVisualizationType } from './views/BaseVisualization/types';
import { type View } from 'common/types/view';
import { type ViewColumn, type ColumnFormat } from 'common/types/viewColumn';

// Constants
const WINDOW_RESIZE_RERENDER_DELAY = 200;

interface Domain {
  min: number;
  max: number;
}

interface BucketingOptions {
  bucketType: string;
  bucketSize?: number;
}

interface Bucket {
  start: number;
  end: number;
  value: number;
}

interface BucketedDataResult {
  bucketedData: Bucket[];
  bucketingOptions: BucketingOptions;
}

/** The shape returned by makeSocrataDataRequest and consumed by the visualization/summary table. */
interface HistogramData {
  bucketType: string;
  rows: DataRow[];
  columns: string[];
  columnFormats?: { [fieldName: string]: ColumnFormat };
}

interface VizError {
  vifValidatorErrors?: string[];
  soqlError?: { errorCode?: string };
  useGenericErrorViz?: boolean;
}

type RenderVifEvent = {
  originalEvent?: {
    detail?: Vif;
  };
} & JQuery.TriggeredEvent;

/**
 * Requests the data required to render the chart, transforms it, and returns a Promise that
 * asynchronously signals success or failure.
 *
 * There are two ways this function will bucket the data: linearly and logarithmically.  Linear
 * buckets all have the same width, and logarithmic buckets have sizes of increasing powers of 10.
 * The bucketing scheme will be automatically determined using a heuristic, but can be overriden by
 * passing in the configuration option "bucketType" in the vif.  The bucketing methods correspond to
 * signed_magnitude_10 and signed_magnitude_linear SoQL functions.
 *
 * On success, the "bucketedData" property will contain the bucketed data, which is an array of buckets.
 * Each bucket has three entries: "start", indicating the lower bound of
 * the bucket's range, "end", indicating the upper bound of the bucket's range, and "value",
 * indicating the value on the y-axis for that bucket.
 */
function getData(vif: Vif, seriesIndex: number): Promise<BucketedDataResult> {
  // We're able to automatically detect a bucketType, but it's also possible to explicitly override
  // this. This override is stored in the vif's configuration.
  const bucketTypeOverride = _.get(vif, 'configuration.bucketType');

  // First, fetch the min and max of the column
  return (
    fetchDomain(vif, seriesIndex)
      // Then transform this into an object with information about the bucketType and bucketSize,
      // also passing in an override bucketType from the vif in the event it has been explicitly set.
      .then((domain) => DistributionChartHelpers.getBucketingOptions(domain, bucketTypeOverride, vif))
      // Make the appropriate query to fetch bucketed data once bucketing scheme is known
      .then((bucketingOptions) => fetchBucketedData(bucketingOptions, vif, seriesIndex))
  );
}

function dataProvider(vif: Vif, seriesIndex: number): SoqlDataProvider {
  return new SoqlDataProvider({
    datasetUid: getDatasetUid(vif),
    domain: getDomain(vif),
    clientContextVariables: getParameterOverrides(vif, seriesIndex),
  });
}

/**
 * Using the current data providers and vif, fetches the min and max of the column.  Returns a
 * Promise which will resolve with an object containing "min" and "max" keys.
 */
function fetchDomain(vif: Vif, seriesIndex: number): Promise<Domain> {
  const column = SoqlHelpers.dimension(vif, seriesIndex);
  const queryParts = [`SELECT min(${column}) as \`min\`, max(${column}) as \`max\``];
  const whereClause = SoqlHelpers.whereClauseFilteringOwnColumn(vif, seriesIndex);

  if (whereClause.length > 0) {
    queryParts.push(`WHERE ${whereClause}`);
  }

  const columnNames = ['min', 'max'];
  const queryString = queryParts.join(' ');

  return (
    dataProvider(vif, seriesIndex) // TODO negative domain.
      .getRows(columnNames, queryString)
      // Convert the SoqlDataProvider response into an object containing min and max keys.
      // _.map (rather than native .map) tolerates an empty response, where _.head is undefined.
      .then((response) => _.map(_.head(response.rows) as string[], (value) => parseFloat(value)))
      .then((values) => _.zipObject(columnNames, values) as unknown as Domain)
  );
}

/**
 * Given a set of bucketingOptions, makes the SoQL requests to bucket the data appropriately and
 * returns a Promise containing the results.  The Promise will resolve with an object:
 * {
 *   bucketingOptions: Pass-through of bucketingOptions parameter.
 *   bucketedData: Array of buckets.
 * }
 * @param bucketingOptions
 * @param bucketingOptions.bucketType - Either "linear" or "logarithmic"
 * @param bucketingOptions.bucketSize - If bucketType is "linear", the size of each bucket.
 */
function fetchBucketedData(
  bucketingOptions: BucketingOptions,
  vif: Vif,
  seriesIndex: number,
): Promise<BucketedDataResult> {
  const soqlDataProvider = dataProvider(vif, seriesIndex);

  const whereClauseComponents = SoqlHelpers.whereClauseFilteringOwnColumn(vif, seriesIndex);

  const whereClause = whereClauseComponents.length > 0 ? `WHERE ${whereClauseComponents}` : '';

  const queryParameters: {
    column: string;
    columnAlias: string;
    value: string;
    valueAlias: string;
    whereClause: string;
    bucketingFunction?: string;
  } = {
    column: SoqlHelpers.dimension(vif, seriesIndex),
    columnAlias: '__magnitude__',
    value: SoqlHelpers.aggregationClause(vif, seriesIndex, 'measure'),
    valueAlias: '__value__',
    whereClause,
  };

  if (bucketingOptions.bucketType === 'linear') {
    queryParameters.bucketingFunction = `floor(${queryParameters.column}/${bucketingOptions.bucketSize})`;
  } else {
    queryParameters.bucketingFunction = `signed_magnitude_10(${queryParameters.column})`;
  }

  const queryTemplate = [
    'select {bucketingFunction} as {columnAlias}, ',
    '{value} as {valueAlias} ',
    '{whereClause} group by {columnAlias} order by {columnAlias} limit 200',
  ].join('');

  const filteredDataQuery = formatString(queryTemplate, queryParameters);

  return soqlDataProvider
    .query(filteredDataQuery, queryParameters.columnAlias, queryParameters.valueAlias)
    .then((soqlData) => transformBucketedData(bucketingOptions, soqlData));
}

/**
 * Given an object specifying bucketingOptions and data response,
 * transforms the data into an object with the bucketed data (array of buckets).
 * @param bucketingOptions
 * @param response
 * @throws
 */
function transformBucketedData(
  bucketingOptions: BucketingOptions,
  response: { rows: unknown[][] },
): BucketedDataResult {
  // Transform the array of arrays into an array of objects, each with 'magnitude' and 'value' keys.
  const data = _.chain(response.rows)
    .map((pair) => _.map(pair, parseFloat))
    .map((parsed) => _.zipObject(['magnitude', 'value'], parsed))
    .value();

  const bucketedData = DistributionChartHelpers.bucketData(data, bucketingOptions);

  if (!_.isArray(bucketedData)) {
    throw new Error('Cannot render distribution chart: data is empty');
  }

  return {
    bucketedData,
    bucketingOptions,
  };
}

$.fn.socrataSvgHistogram = function (this: JQuery, originalVif: Vif, options: any): JQuery {
  originalVif = migrateVif(originalVif);
  const $element = $(this);
  // SvgHistogram mixes in BaseVisualization methods via `_.extend` in its (still-JS)
  // constructor, so TypeScript can't see them on the instance type.
  const visualization = new SvgHistogram($element, originalVif, options) as InstanceType<
    typeof SvgHistogram
  > &
    BaseVisualizationType;
  let rerenderOnResizeTimeout: ReturnType<typeof setTimeout>;

  /**
   * Event handling
   */

  function attachEvents() {
    // Destroy on (only the first) 'SOCRATA_VISUALIZATION_DESTROY' event.
    $element.one('SOCRATA_VISUALIZATION_DESTROY', function () {
      clearTimeout(rerenderOnResizeTimeout);
      visualization.destroy();
      detachEvents();
    });

    $(window).on('resize', handleWindowResize);

    $element.on('SOCRATA_VISUALIZATION_INVALIDATE_SIZE', visualization.invalidateSize);
    $element.on('SOCRATA_VISUALIZATION_RENDER_VIF', handleRenderVif);
  }

  function detachEvents() {
    $(window).off('resize', handleWindowResize);

    $element.off('SOCRATA_VISUALIZATION_INVALIDATE_SIZE', visualization.invalidateSize);
    $element.off('SOCRATA_VISUALIZATION_RENDER_VIF', handleRenderVif);
  }

  function handleWindowResize() {
    clearTimeout(rerenderOnResizeTimeout);

    rerenderOnResizeTimeout = setTimeout(
      visualization.render() as unknown as () => void,
      // Add some jitter in order to make sure multiple visualizations are
      // unlikely to all attempt to rerender themselves at the exact same
      // moment.
      WINDOW_RESIZE_RERENDER_DELAY + Math.floor(Math.random() * 10),
    );
  }

  function handleRenderVif(event: JQuery.TriggeredEvent) {
    const newVif = (event as RenderVifEvent).originalEvent!.detail!;

    updateData(migrateVif(newVif));
  }

  function handleError(error: VizError) {
    let messages;

    if (window.console && console.error) {
      console.error(error);
    }

    if (error.vifValidatorErrors) {
      messages = error.vifValidatorErrors;
    } else if (error.soqlError) {
      const errorCode = _.get(error, 'soqlError.errorCode');

      messages = errorCode
        ? I18n.t(`shared.visualizations.charts.common.soql_error.${errorCode}`)
        : I18n.t('shared.visualizations.charts.common.error_generic');
    } else {
      return visualization.renderGenericError(
        I18n.t('shared.errors.private_or_deleted_asset.message'),
        'privateOrDeletedAsset',
      );
    }

    if (error.useGenericErrorViz) {
      visualization.renderGenericError(messages, 'histogram');
    } else {
      visualization.renderError(messages);
    }
  }

  function updateData(newVif: Vif) {
    $element.trigger('SOCRATA_VISUALIZATION_DATA_LOAD_START');
    visualization.showBusyIndicator();

    $.fn.socrataSvgHistogram
      .validateVif(newVif)
      .then(() => {
        const datasetMetadataProvider = new MetadataProvider(
          {
            domain: _.get(newVif, 'series[0].dataSource.domain'),
            datasetUid: _.get(newVif, 'series[0].dataSource.datasetUid'),
          },
          true,
        );

        const dataRequests = newVif.series.map(function (series, seriesIndex) {
          switch (series.dataSource.type) {
            case 'socrata.soql':
              return makeSocrataDataRequest(newVif, seriesIndex);

            default:
              return Promise.reject(
                `Invalid/unsupported series dataSource.type: "${series.dataSource.type}".`,
              );
          }
        });

        const displayableFilterableColumns = visualization.shouldDisplayFilterBar()
          ? datasetMetadataProvider.getDisplayableFilterableColumns()
          : null;

        const getFormattedComputedRegionColumnsPromise =
          datasetMetadataProvider.getFormattedComputedRegionColumns();

        return Promise.all([
          displayableFilterableColumns,
          datasetMetadataProvider.getDatasetMetadata(),
          getFormattedComputedRegionColumnsPromise,
          ...dataRequests,
        ]).then(function (resolutions) {
          const [newColumns, datasetMetadata, newComputedColumns, ...dataResponses] = resolutions as [
            ViewColumn[] | null,
            View,
            ViewColumn[],
            ...HistogramData[],
          ];

          const displayableColumns = getDisplayableColumns(datasetMetadata);
          dataResponses[0].columnFormats = ColumnFormattingHelpers.getColumnFormats(displayableColumns);

          const newTableVif = generateHistorgramSummaryTableVif(
            newVif,
            datasetMetadata,
            dataResponses,
            displayableColumns,
          );

          $element.trigger('SOCRATA_VISUALIZATION_DATA_LOAD_COMPLETE');
          visualization.hideBusyIndicator();

          visualization.render({
            newColumns,
            newComputedColumns,
            newData: dataResponses,
            newVif,
            newTableVif,
          });
        });
      })
      .catch(handleError);
  }

  /**
   * Generates a summary table VIF for Histograms
   */
  function generateHistorgramSummaryTableVif(
    newVif: Vif,
    datasetMetadata: View,
    dataResponses: HistogramData[],
    displayableColumns: ViewColumn[],
  ) {
    const newData = dataResponses[0];

    const dimensionColumnName = _.get(newVif, 'series[0].dataSource.dimension.columnName');
    const dimension = _.find(datasetMetadata.columns, (column) => dimensionColumnName === column.fieldName);

    /** Set dimension to always render as a text for summary tables */
    const tableDimension = _.cloneDeep(dimension) as ViewColumn;
    _.set(tableDimension, 'renderTypeName', SoQLType.SoQLTextT);

    const tableData = _.cloneDeep(newData);

    /** Merge the start and end of the histogram bucket into one column */
    _.set(
      tableData,
      'rows',
      newData.rows.map((column) => [`${column[0]} to ${column[1]}`, column[2]]),
    );

    return generateSummaryTableVif(
      newVif,
      displayableColumns,
      tableData as Parameters<typeof generateSummaryTableVif>[2],
      tableDimension,
    );
  }

  function makeSocrataDataRequest(vifToRender: Vif, seriesIndex: number): Promise<HistogramData> {
    return getData(vifToRender, seriesIndex).then((data) => ({
      bucketType: data.bucketingOptions.bucketType,
      rows: data.bucketedData.map((row) => [row.start, row.end, row.value]),
      columns: ['bucket_start', 'bucket_end', 'measure'],
    }));
  }

  /**
   * Actual execution starts here
   */

  attachEvents();
  updateData(originalVif);

  return this;
  // validateVif is attached to this function immediately below, satisfying the
  // GenericVisualization & { validateVif } type declared in jquery.ts.
} as JQuery['socrataSvgHistogram'];

/**
 * Checks a VIF for compatibility with this visualization. The intent of this
 * function is to provide feedback while authoring a visualization, not to
 * provide feedback to a developer. As such, messages returned are worded to
 * make sense to a user.
 *
 * Returns a Promise.
 *
 * If the VIF is usable, the promise will resolve.
 * If the VIF is not usable, the promise will reject with an object:
 * {
 *   ok: false,
 *   vifValidatorErrors: Array<String>
 * }
 */
$.fn.socrataSvgHistogram.validateVif = (vif: Vif): Promise<any> =>
  // TODO EN-93683 - update once we convert getSoqlVifValidator to typescript
  getSoqlVifValidator(vif).then((validator: any) =>
    validator.requireExactlyOneSeries().requireNumericDimension().requireMeasureAggregation().toPromise(),
  );

export default $.fn.socrataSvgHistogram;
